import { useState } from 'react';
import { linkOptions, useParams } from '@tanstack/react-router';
import { InfluencersTabType } from '@anytag/shared-types';
import { getValuableVariables } from '@anytag/shared-utils';
import { getInfluencerFilterTabUrl, getOpenAiSourceFrom } from '@anytag/shared-organisms';
import { useInfluencerSearchPathnames, useParamsSearch, useQueryHelper } from '@anytag/shared-hooks';
import {
  OpenAIMessage,
  GeminiMessage,
  GeminiNextAction,
  GeminiSourceFrom,
  OpenAINextAction,
  QueryopenAiMessageArgs,
  useOpenAiMessagePromiseQuery,
  useSendOpenAiMessageMutation,
  useSendGeminiMessageMutation
} from '@anytag/graphql';
import { MessageType } from '../chat-room';
import { useAIAssistantStates } from './use-ai-assistant-states';

const MAX_RETRY = 5;

export const useInitAIAssistant = () => {
  const { t, navigate } = useQueryHelper();
  const params = useParams({ strict: false });
  const [retryCounter, setRetryCounter] = useState(MAX_RETRY);
  const { limit, filter, aiAssistantState } = useParamsSearch();
  const { getOpenAiMessage } = useOpenAiMessagePromiseQuery();
  const { callSendOpenAiMessage, loading: isSendingOpenAiMessage } = useSendOpenAiMessageMutation({
    notifyOnNetworkStatusChange: true
  });
  const { callSendGeminiMessage, loading: isSendingGeminiMessage } = useSendGeminiMessageMutation({
    notifyOnNetworkStatusChange: true
  });
  const { isSupportedChatAIPaths, isInfluencersSearchPage, isEngagementSearchPage } = useInfluencerSearchPathnames();
  const {
    error,
    threadId,
    messages,
    isAiSidebarOpen,
    isOpenAiModel,
    defaultTemplate,
    isChatHistoryOpen,
    isCreatingChatAi,
    chatHistoryThreadId,
    isLoadingChatHistory,
    setError,
    closeChat,
    clearError,
    clearMessages,
    getChatHistory,
    refreshChat,
    setChatHistoryThreadId,
    createChatAiThread,
    removeLastTypingMessage,
    setAiAssistantState,
    updateMessages,
    setDefaultTemplate
  } = useAIAssistantStates();

  const tab = filter.tab;
  const sourceFrom = getOpenAiSourceFrom(tab);

  const generateInfluencerSearchRoute = (routeParams: { tab: InfluencersTabType; includeIds?: readonly number[] }) => {
    const route = isInfluencersSearchPage
      ? linkOptions({
          to: getInfluencerFilterTabUrl(routeParams.tab),
          search: { filter: routeParams, limit }
        })
      : isEngagementSearchPage
        ? linkOptions({
            to: getInfluencerFilterTabUrl(routeParams.tab, true),
            params,
            search: { filter: routeParams, limit }
          })
        : null;

    if (!route) {
      setError({ message: t('Unsupported search on this page') });
    }

    return route;
  };

  const resetCurrentSearchParams = () => {
    if (tab) {
      // Current requirement is clear all the filter when chatbox closed
      // https://adasiaholdings.atlassian.net/browse/AT-5859
      const route = generateInfluencerSearchRoute({ tab });

      if (route) {
        navigate(route);
      }
    }
  };

  const resetRetryCounter = () => {
    if (retryCounter < MAX_RETRY) {
      setRetryCounter(MAX_RETRY);
    }
  };

  const handleResponse = (response: OpenAIMessage | GeminiMessage) => {
    switch (response.nextAction) {
      case GeminiNextAction.ERROR:
      case OpenAINextAction.ERROR:
      case OpenAINextAction.WAITING_FOR_RESPONSE:
        setError(
          isOpenAiModel
            ? { type: 'generate-failed', runId: (response as OpenAIMessage)?.runId }
            : { message: 'Something went wrong!' }
        );
        break;
      case GeminiNextAction.READY_TO_SEARCH:
      case OpenAINextAction.READY_TO_SEARCH:
        {
          const route = tab ? generateInfluencerSearchRoute({ tab, includeIds: response.influencerIds }) : '';

          if (response.text) {
            updateMessages([{ position: 'left', text: response.text, copyable: true, regenerative: true }]);
          }

          if (route) {
            removeLastTypingMessage();
            navigate({
              ...route,
              search: { ...route.search, aiAssistantState, state: { skipSearch: !response.influencerIds.length } }
            });
          }

          resetRetryCounter();
        }
        break;
      case GeminiNextAction.COLLECTING:
      case OpenAINextAction.COLLECTING:
      default:
        if (response.text) {
          updateMessages([{ position: 'left', text: response.text, copyable: true, regenerative: true }]);
        } else {
          setError({ message: t("OpenAi doesn't respond") });
        }
        resetRetryCounter();
        break;
    }
  };

  const resendOpenAiMessage = async (runId?: QueryopenAiMessageArgs['runId']) => {
    if (!threadId || !runId) {
      return;
    }

    updateMessages([{ position: 'left', typing: true }]);

    try {
      const { data: openAiData } = await getOpenAiMessage({
        variables: { runId, sourceFrom, threadSystemId: threadId }
      });

      if (openAiData.openAiMessage) {
        handleResponse(openAiData.openAiMessage);
      } else {
        setError({ type: 'generate-failed' });
      }
    } catch (error) {
      setError({ message: t(error.message || 'Got unknown error after recall api') });
    }
  };

  const sendChatAiMessage = async (chatBoxMessage?: MessageType) => {
    const message = chatBoxMessage?.text;
    const imageUrl = (chatBoxMessage?.file?.type === 'image' && chatBoxMessage.file.url) || '';

    if (!threadId || !(message || imageUrl)) return;

    updateMessages([{ position: 'left', typing: true }]);

    try {
      if (isOpenAiModel) {
        const { data } = await callSendOpenAiMessage({
          variables: {
            input: getValuableVariables({ id: threadId, message, imageUrl, sourceFrom })
          }
        });

        if (data?.sendOpenAiMessage?.message) {
          const response = { ...data.sendOpenAiMessage.message };

          // AI still process the answer, we need to call another API to get answer
          if (response.nextAction === OpenAINextAction.WAITING_FOR_RESPONSE) {
            await resendOpenAiMessage(response.runId);
          } else {
            handleResponse(response);
          }
        } else {
          setError({ type: 'generate-failed' });
        }
      } else {
        const { data } = await callSendGeminiMessage({
          variables: {
            input: getValuableVariables({
              id: threadId,
              message,
              imageUrl,
              sourceFrom: sourceFrom as unknown as GeminiSourceFrom
            })
          }
        });

        if (data?.sendGeminiMessage?.message) {
          handleResponse(data.sendGeminiMessage.message);
        } else {
          setError({ message: 'Something went wrong!' });
        }
      }
    } catch (error) {
      setError({ message: t(error.message || 'Got unknown error after call api') });
    }
  };

  const addNewMessages = async (newMessages: MessageType[]) => {
    if (!newMessages.length || !threadId) return;

    if (error) {
      clearError();
    }

    updateMessages(newMessages); // Always add new message into the list

    // Only call api if messages come from 'right' position. Mean sent by user
    const isFiles = newMessages.every((message) => !!message.file && !message.text && message.position === 'right');
    const isMessage = newMessages.length === 1 && !!newMessages.at(0)?.text && newMessages.at(0)?.position === 'right';

    if (!isFiles && !isMessage) {
      return;
    }

    await sendChatAiMessage(newMessages.at(0));

    // if message choosed from template, clear the template after message sent
    if (defaultTemplate) {
      setDefaultTemplate('');
    }
  };

  const regenerateResponse = async () => {
    // Backup runId then clear error to return back screen first before calling resend api
    const runId = error?.runId;
    clearError();

    if (runId) {
      await resendOpenAiMessage(runId);
    } else {
      await sendChatAiMessage({ position: 'right', text: 'Hello!' });
    }

    setRetryCounter((prevCounter) => prevCounter--);
  };

  const regenerateMessage = async (index: number) => {
    const message = messages.at(index);
    const previousMessage = messages.at(index - 1);

    if (
      !index || // Ignore first default hello message
      !message ||
      message.typing ||
      !message.regenerative ||
      !previousMessage || // We need to resend previous message again
      !(previousMessage.text || previousMessage.file) || // it has to have text or file
      previousMessage.position !== 'right' // must be sent by user
    )
      return;

    clearMessages(index);
    await sendChatAiMessage(previousMessage);
  };

  return {
    addNewMessages,
    closeChat,
    error,
    defaultTemplate,
    isAiSidebarOpen,
    isChatHistoryOpen,
    isCreatingChatAi,
    isLoadingChatHistory,
    isOpenAiModel,
    chatHistoryThreadId,
    isSupportedChatAIPaths,
    messages,
    createChatAiThread,
    refreshChat,
    setChatHistoryThreadId,
    regenerateMessage,
    getChatHistory,
    regenerateResponse: retryCounter ? regenerateResponse : undefined,
    isSendingMessage: isSendingOpenAiMessage || isSendingGeminiMessage,
    setError,
    setDefaultTemplate,
    resetCurrentSearchParams,
    threadId,
    setAiAssistantState
  };
};

export type UseInitAIAssistant = ReturnType<typeof useInitAIAssistant>;
